/**
 * Utility class for transforming and validating table assignments
 * Handles mapping between UI state and API/storage formats
 */
export class TableAssignmentMapper {
  /**
   * Transform assignments for API consumption
   * Filters out incomplete mappings and formats for regeneration
   * @param {Array} assignments - Raw table assignments from UI
   * @returns {Array} Filtered and formatted assignments
   */
  static prepareForInsertAll(assignments) {
    if (!Array.isArray(assignments)) {
      console.warn('TableAssignmentMapper: Invalid assignments array')
      return []
    }

    // Filter assignments that have both template and replacement tables
    const validAssignments = assignments.filter(assignment => {
      const hasTemplateTable = assignment.templateTableId || assignment.templateTable
      const hasReplacementTable = assignment.replacementTableId || assignment.replacementTable
      return hasTemplateTable && hasReplacementTable
    })

    // Map to the expected format
    return validAssignments.map(assignment => ({
      id: assignment.id || '',
      section: assignment.section || '',
      templateTable: assignment.templateTable || '',
      templateTableId: assignment.templateTableId || '',
      replacementTable: assignment.replacementTable || '',
      replacementTableId: assignment.replacementTableId || '',
      instructions: assignment.instructions || '',
      isUserAdded: assignment.isUserAdded || false
    }))
  }

  /**
   * Prepare assignments for localStorage
   * Preserves all data including incomplete mappings
   * @param {Array} assignments - Table assignments from UI
   * @returns {Array} Assignments ready for storage
   */
  static prepareForStorage(assignments) {
    if (!Array.isArray(assignments)) {
      return []
    }

    console.log('crypto.randomUUID', crypto.randomUUID())

    return assignments.map(assignment => ({
      id: assignment.id,
      section: assignment.section || '',
      templateTable: assignment.templateTable || '',
      templateTableId: assignment.templateTableId || '',
      replacementTable: assignment.replacementTable || '',
      replacementTableId: assignment.replacementTableId || '',
      instructions: assignment.instructions || '',
      isUserAdded: assignment.isUserAdded || false // Preserve user-added flag
    }))
  }

  /**
   * Initialize assignments from document MRT tables
   * @param {Array} documentMRTTables - Array of MRT table objects
   * @param {Object} sourceDocument - Source document object
   * @param {Array} existingAssignments - Existing assignments to preserve user-added rows
   * @returns {Array} Initial table assignments
   */
  static initializeFromMRTTables(documentMRTTables, sourceDocument, existingAssignments = []) {
    if (!Array.isArray(documentMRTTables)) {
      return []
    }

    let filteredTables = documentMRTTables

    // Filter by source document path if available
    if (sourceDocument?.document_source_path) {
      const normalizedPath = sourceDocument.document_source_path
        .split("/")
        .slice(1)
        .join("/")
      
      filteredTables = documentMRTTables.filter(table => 
        table.file_path === normalizedPath || table.file_path
      )
    }

    // Map to assignment format
    const newAssignments = filteredTables.map(table => ({
      id: crypto.randomUUID ? crypto.randomUUID() : `assignment-${Date.now()}-${Math.random()}`,
      section: table.section_number || '',
      templateTable: table.uuid || '',
      templateTableId: table.id || '',
      replacementTable: '',
      replacementTableId: '',
      instructions: '',
      isUserAdded: false // Mark as not user-added
    }))

    // Preserve user-added rows from existing assignments
    const userAddedRows = existingAssignments.filter(assignment => assignment.isUserAdded === true)
    
    // Combine new assignments with preserved user-added rows
    return [...newAssignments, ...userAddedRows]
  }

  /**
   * Merge stored assignments with current MRT tables
   * Preserves user modifications while updating with new tables
   * @param {Array} storedAssignments - Assignments from localStorage
   * @param {Array} currentAssignments - Current assignments from MRT tables
   * @returns {Array} Merged assignments
   */
  static mergeAssignments(storedAssignments, currentAssignments) {
    if (!storedAssignments || !storedAssignments.length) {
      return currentAssignments
    }

    const mergedMap = new Map()
    
    // Add current assignments first
    currentAssignments.forEach(assignment => {
      const key = assignment.templateTableId || assignment.templateTable
      mergedMap.set(key, assignment)
    })

    // Override with stored assignments (preserving user changes)
    storedAssignments.forEach(stored => {
      const key = stored.templateTableId || stored.templateTable
      if (key) {
        // Check if this table still exists in current assignments
        if (mergedMap.has(key)) {
          // Merge: keep stored replacement and instructions
          const current = mergedMap.get(key)
          mergedMap.set(key, {
            ...current,
            replacementTable: stored.replacementTable,
            replacementTableId: stored.replacementTableId,
            instructions: stored.instructions
          })
        } else {
          // Table no longer in MRT tables, but keep the stored assignment
          mergedMap.set(key, stored)
        }
      }
    })

    return Array.from(mergedMap.values())
  }

  /**
   * Validate assignment completeness
   * @param {Object} assignment - Single assignment object
   * @returns {boolean} True if assignment is complete
   */
  static isComplete(assignment) {
    return !!(
      assignment.templateTableId && assignment.replacementTableId
    )
  }

  /**
   * Get statistics about assignments
   * @param {Array} assignments - Array of assignments
   * @returns {Object} Statistics object
   */
  static getStatistics(assignments) {
    if (!Array.isArray(assignments)) {
      return {
        total: 0,
        complete: 0,
        incomplete: 0,
        withInstructions: 0
      }
    }

    const complete = assignments.filter(a => this.isComplete(a))
    const withInstructions = assignments.filter(a => a.instructions && a.instructions.trim())

    return {
      total: assignments.length,
      complete: complete.length,
      incomplete: assignments.length - complete.length,
      withInstructions: withInstructions.length
    }
  }

  /**
   * Sort assignments by section number
   * @param {Array} assignments - Array of assignments
   * @returns {Array} Sorted assignments
   */
  static sortBySectionNumber(assignments) {
    if (!Array.isArray(assignments)) {
      return []
    }

    return [...assignments].sort((a, b) => {
      // Handle empty sections - they go to the bottom
      if (!a.section && !b.section) return 0
      if (!a.section) return 1
      if (!b.section) return -1
      
      // Parse section numbers into arrays of numbers
      const aSections = a.section.split('.').map(num => {
        const parsed = parseInt(num)
        return isNaN(parsed) ? 0 : parsed
      })
      const bSections = b.section.split('.').map(num => {
        const parsed = parseInt(num)
        return isNaN(parsed) ? 0 : parsed
      })
      
      // Compare section by section
      const maxLength = Math.max(aSections.length, bSections.length)
      for (let i = 0; i < maxLength; i++) {
        const aNum = aSections[i] || 0
        const bNum = bSections[i] || 0
        
        if (aNum !== bNum) {
          return aNum - bNum
        }
      }
      
      // If sections are equal, maintain original order (stable sort)
      return 0
    })
  }

  /**
   * Create API payload for regeneration
   * @param {string} documentId - Document ID
   * @param {Array} mappings - Prepared mappings from prepareForInsertAll
   * @returns {Object} API payload
   */
  static createRegenerationPayload(documentId, mappings) {
    const regenerationRequests = mappings.map(mapping => ({
      regeneration_type: "tfl_update",
      connector_updates: {
        old_connector_data_id: [
          mapping.templateTableId
        ],
      },
      new_connector_data_id: mapping.replacementTableId,
    }))

    return {
      document_id: documentId,
      regeneration_requests: regenerationRequests
    }
  }
}